//ts-check
import {move} from '@dnd-kit/helpers'

export const MAX_BRANDS_TO_DISPLAY = 6

/**
 * Sort catalog items by ascending display order, pushing items without a valid order to the end.
 *
 * @template {{catalogDisplayOrder?: number}} T
 * @param {T[]} [items=[]]
 * @returns {T[]}
 */
export const sortByDisplayOrder = (items = []) => {
  return [...items].sort((left, right) => {
    const leftOrder = Number.isFinite(left?.catalogDisplayOrder) ? left.catalogDisplayOrder : Number.MAX_SAFE_INTEGER
    const rightOrder = Number.isFinite(right?.catalogDisplayOrder) ? right.catalogDisplayOrder : Number.MAX_SAFE_INTEGER

    return leftOrder - rightOrder
  })
}

/**
 * Sort brands, categories, and subcategories into the initial UI order.
 *
 * @param {import('@/types/productCatalogTypes').ProductCategoriesData} catalogData
 * @returns {import('@/types/productCatalogTypes').ProductCategoriesData}
 */
export const sortCatalogData = catalogData => ({
  brands: sortByDisplayOrder(catalogData?.brands ?? []),
  categories: sortByDisplayOrder(catalogData?.categories ?? []).map(category => ({
    ...category,
    subCategories: sortByDisplayOrder(category?.subCategories ?? []),
  })),
})

/**
 * Rewrite catalogDisplayOrder values to match the current array order.
 *
 * @template {{catalogDisplayOrder?: number}} T
 * @param {T[]} [items=[]]
 * @returns {(T & {catalogDisplayOrder: number})[]}
 */
export const updateDisplayOrder = items => {
  return (items ?? []).map((item, index) => ({
    ...item,
    catalogDisplayOrder: index + 1,
  }))
}

/**
 * Reorder catalog items after a drag event and persist the new display order values.
 *
 * @template {{catalogDisplayOrder?: number}} T
 * @param {T[]} [items=[]]
 * @param {import('@dnd-kit/dom').DragEndEvent} event
 * @returns {(T & {catalogDisplayOrder: number})[]}
 */
export const reorderCatalogItems = (items, event) => {
  return updateDisplayOrder(move(items ?? [], event))
}

/**
 * Move an item to a one-based sibling position, preserving everything outside
 * the affected range. Invalid destinations must be handled by the input/drop UI.
 *
 * @template {{id: string, catalogDisplayOrder?: number}} T
 * @param {T[]} items
 * @param {string} id
 * @param {number} position
 * @returns {T[]}
 */
export const moveCatalogItem = (items, id, position) => {
  const index = items.findIndex(item => item.id === id)
  if (index < 0 || !Number.isSafeInteger(position) || position < 1 || position > items.length) {
    throw new RangeError('Catalog position must identify an item within its sibling list.')
  }
  if (index === position - 1) return items
  const next = [...items]
  const [item] = next.splice(index, 1)
  next.splice(position - 1, 0, item)
  return updateDisplayOrder(next)
}

/**
 * @param {string} id
 * @param {string | null} [parentId]
 */
export const getOrderItemKey = (id, parentId = null) => JSON.stringify([parentId, id])

/** @param {import('@/types/productCatalogTypes').ProductCategory[]} categories */
export const getCatalogPositions = categories => {
  const positions = new Map()
  categories.forEach((category, index) => {
    positions.set(getOrderItemKey(category.id), index + 1)
    category.subCategories.forEach((subcategory, subIndex) => {
      positions.set(getOrderItemKey(subcategory.id, category.id), subIndex + 1)
    })
  })
  return positions
}

/**
 * Move a category, or a subcategory when parentId is given, to a one-based position.
 * Returns the same array when nothing changes.
 *
 * @param {import('@/types/productCatalogTypes').ProductCategory[]} categories
 * @param {string} id
 * @param {number} position
 * @param {string | null} [parentId]
 */
export const moveCategoryItem = (categories, id, position, parentId = null) => {
  if (parentId === null) return moveCatalogItem(categories, id, position)

  const parent = categories.find(category => category.id === parentId)
  if (!parent) throw new Error('Cannot reorder a subcategory without its parent.')

  const reordered = moveCatalogItem(parent.subCategories, id, position)
  if (reordered === parent.subCategories) return categories
  return categories.map(category => (category.id === parentId ? {...category, subCategories: reordered} : category))
}

/**
 * Track which items the user moved directly. Items that end up back in their
 * original position are dropped from the set.
 *
 * @param {Set<string>} directlyMoved
 * @param {string} id
 * @param {string | null} parentId
 * @param {import('@/types/productCatalogTypes').ProductCategory[]} originalCategories
 * @param {import('@/types/productCatalogTypes').ProductCategory[]} nextCategories
 */
export const getDirectlyMoved = (directlyMoved, id, parentId, originalCategories, nextCategories) => {
  const next = new Set([...directlyMoved, getOrderItemKey(id, parentId)])
  const originalPositions = getCatalogPositions(originalCategories)
  const finalPositions = getCatalogPositions(nextCategories)
  next.forEach(key => {
    if (originalPositions.get(key) === finalPositions.get(key)) next.delete(key)
  })
  return next
}

/**
 * @param {import('@/types/productCatalogTypes').ProductCategory[]} categories
 * @param {string} categoryId
 * @param {string} subcategoryId
 */
export const toggleSubcategoryVisible = (categories, categoryId, subcategoryId) => {
  return categories.map(category =>
    category.id === categoryId
      ? {
          ...category,
          subCategories: category.subCategories.map(subcategory =>
            subcategory.id === subcategoryId ? {...subcategory, visible: !subcategory.visible} : subcategory
          ),
        }
      : category
  )
}

/**
 * Reorder subcategories for a single category and update their catalogDisplayOrder values.
 *
 * @param {import('@/types/productCatalogTypes').ProductCategory[]} [categories=[]]
 * @param {string} categoryId
 * @param {import('@dnd-kit/dom').DragEndEvent} event
 * @returns {import('@/types/productCatalogTypes').ProductCategory[]}
 */
export const reorderCategorySubcategories = (categories, categoryId, event) => {
  return (categories ?? []).map(category => {
    if (category.id !== categoryId) {
      return category
    }

    return {
      ...category,
      subCategories: reorderCatalogItems(category.subCategories ?? [], event),
    }
  })
}

/**
 * Resolve the preferred display name for a catalog item.
 *
 * @param {{id?: string, name?: import('@/types/productCatalogTypes').ProductCategoryLocalizedName}} item
 * @returns {string}
 */
export const getCatalogItemName = item => {
  return item?.name?.en ?? item?.name?.es_MX ?? item?.id ?? ''
}

/**
 * Count hidden subcategories within a category.
 *
 * @param {import('@/types/productCatalogTypes').ProductSubcategory[]} [subcategories=[]]
 * @returns {number}
 */
export const getHiddenSubcategoriesCount = subcategories => {
  return (subcategories ?? []).filter(subcategory => !subcategory.visible).length
}

/**
 * Format a numeric position using an ordinal suffix.
 *
 * @param {number} position
 * @returns {string}
 */
export const getPositionLabel = position => {
  const remainderTen = position % 10
  const remainderHundred = position % 100

  if (remainderTen === 1 && remainderHundred !== 11) {
    return `${position}st Position`
  }

  if (remainderTen === 2 && remainderHundred !== 12) {
    return `${position}nd Position`
  }

  if (remainderTen === 3 && remainderHundred !== 13) {
    return `${position}rd Position`
  }

  return `${position}th Position`
}

/**
 * Convert catalog UI state into the backend publish payload.
 *
 * @param {{
 *   brands?: import('@/types/productCatalogTypes').ProductCategoryBrand[],
 *   categories?: import('@/types/productCatalogTypes').ProductCategory[],
 * }} catalogData
 * @returns {{
 *   items: Array<
 *     | {id: string, type: 'displaybrand', catalogDisplayOrder: number}
 *     | {
 *         id: string,
 *         type: 'displaycategory',
 *         catalogDisplayOrder: number,
 *         items: Array<{id: string, catalogDisplayOrder: number, visible: boolean}>
 *       }
 *   >
 * }}
 */
export const transformCatalogToPublishPayload = catalogData => {
  const orderedBrands = updateDisplayOrder(catalogData?.brands ?? [])
  const orderedCategories = updateDisplayOrder(catalogData?.categories ?? []).map(category => ({
    ...category,
    subCategories: updateDisplayOrder(category.subCategories ?? []),
  }))

  const brandItems = orderedBrands.map(brand => ({
    id: brand.id,
    type: 'displaybrand',
    catalogDisplayOrder: brand.catalogDisplayOrder,
  }))

  const categoryItems = orderedCategories.map(category => ({
    id: category.id,
    type: 'displaycategory',
    catalogDisplayOrder: category.catalogDisplayOrder,
    items: (category.subCategories ?? []).map(subcategory => ({
      id: subcategory.id,
      catalogDisplayOrder: subcategory.catalogDisplayOrder,
      visible: Boolean(subcategory.visible),
    })),
  }))

  return {
    items: [...brandItems, ...categoryItems],
  }
}

/**
 * Count changed persisted entities, including siblings shifted by a move.
 * Images are saved separately and are deliberately excluded.
 *
 * @param {import('@/types/productCatalogTypes').ProductCategoriesData} baseline
 * @param {import('@/types/productCatalogTypes').ProductCategoriesData} draft
 */
export const countCatalogChanges = (baseline, draft) => {
  const flatten = items => {
    const values = new Map()
    items.forEach(item => {
      values.set(JSON.stringify([item.type, item.id]), item.catalogDisplayOrder)
      if ('items' in item) {
        item.items.forEach(subcategory => {
          values.set(
            JSON.stringify([item.id, subcategory.id]),
            `${subcategory.catalogDisplayOrder}:${subcategory.visible}`
          )
        })
      }
    })
    return values
  }
  const previous = flatten(transformCatalogToPublishPayload(baseline).items)
  const current = flatten(transformCatalogToPublishPayload(draft).items)
  return [...new Set([...previous.keys(), ...current.keys()])].filter(key => previous.get(key) !== current.get(key))
    .length
}
